import React from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { getDateProgressPercentage, getKpiIndicator } from "../../utils/helpers";
import { formatDateMedium } from '../../utils/dateFormat';
import { formatFixed } from '../../utils/numberFormat';
import { FIELD_TYPES } from "../../lib/constants";
import styles from './Navigation.module.css'
import { useEffect, useMemo, useState } from "react";
import { CircularProgressbarWithChildren } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import ScheduleThermometer from './ScheduleThermometer';
import ScheduleVariance from './ScheduleVariance';
import BudgetThermometer from './BudgetThermometer';
import BudgetOverviewBar from '../BudgetOverview/BudgetOverviewBar';
import BudgetVariance from './BudgetVariance';
import { kpiConfig } from "../../Data/KpiEngine/kpiConfig";
// Seed data imports removed — mission data comes exclusively from the API via project payload.
// import { missionMetricsData } from "../../Data/KpiEngine/missionMetricsData";
// import { missionTargets } from "../../Data/KpiEngine/missionTargets";
import ProjectConfigModal from "../ProjectConfigModal/ProjectConfigModal";
import {
  getProjectKpiConfigOverride,
  upsertProjectKpiConfigOverride,
  type SubKpiInstance,
} from "../../Data/ProjectConfig/projectKpiConfigOverrides";
import {
  getScheduleInstancesFromStorage,
  getInstanceChartDataFromStorage,
} from "../../kpi-engine/scheduleDataAdapter";
import {
  getFinanceInstancesFromStorage,
  getFinanceInstanceChartDataFromStorage,
} from "../../kpi-engine/budgetDataAdapter";

import GroupProgressPage from "../GroupProgressPage/GroupProgressPage";
import { indicatorBucket, bucketToColor } from "../../utils/indicatorBuckets";
import MissionDataEntry from "../MetricEntryGrid/MissionDataEntry";
import MissionHistoryViewer from "../MetricEntryGrid/MissionHistoryViewer";
import MissionSubKpiCard from "../MissionSubKpiCard/MissionSubKpiCard";
// DEV-822: HSE row + bundle helpers. Renders three square cards
// (Health/Safety/Environment) above the Mission KPI rows.
import { HSERow } from "../HSERow/HSERow";
import { ReportIncidentModal } from "../ReportIncidentModal/ReportIncidentModal";
import { EditIncidentModal } from "../EditIncidentModal/EditIncidentModal";
import { IncidentDrillDown } from "../IncidentDrillDown/IncidentDrillDown";
import { computeHseBundle, type HseBundle, type HseSubKpi } from "../../kpi-engine/hse";
import type { IncidentCountConfig } from "../../kpi-engine/missionTypes/incidentCount";
import type { HseCategory } from "../../kpi-engine/missionTypes/types";
import { useHseIncidents } from "../../api/hseIncidents";
// DEV-822 / DEV-816 pattern: pencil-to-configure on HSE cards reuses
// the same Tray + MissionKpiWizard infrastructure as the regular Mission
// card. Wizard is already trimmed for incident_count via prior rounds.
// (getProjectKpiConfigOverride already imported above; here we add the
// upsert helper and the type used by the save handler.)
import { Tray } from "../Tray/Tray";
import { MissionKpiWizard } from "../ProjectConfigModal/MissionKpiWizard";
import { invalidateAndRefetch, QUERY_KEYS } from "../../api/queryCache";
import DataReliabilityTab from "../DataReliabilityTab/DataReliabilityTab";
import { canEditProjectContent, canAdminProject } from "../../security/securityAdapter";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchGanttTasksByProject,
  apiResponseToGanttTask,
} from "../../api/ganttTasks";
import type { GanttTask } from "../../api/ganttTasks";
import PhaseSummaryTimeline from "./PhaseSummaryTimeline";
import CostBreakdownPanel from "./CostBreakdownPanel";
import FundingBreakdownPanel from "./FundingBreakdownPanel";
import EvmLitePanel from "./EvmLitePanel";
import EarnedSchedulePanel from "./EarnedSchedulePanel";
import ProjectStatusReportCard from "../PerformanceSummary/ProjectStatusReportCard";
import MilestoneList from "../PerformanceSummary/MilestoneList";
import MissionStatusCard from "../PerformanceSummary/MissionStatusCard";
import RisksIssuesPlaceholder from "../PerformanceSummary/RisksIssuesPlaceholder";
import ScheduleStatusRow from "../PerformanceSummary/ScheduleStatusRow";
import KpiSummaryHeader from "../KpiSummaryHeader";

// Registry: map configured KPI instance catalog ids to the React component that renders the chart.
// This lets each KPI Category tab host any number of charts of different types.
const KPI_CHART_REGISTRY: Record<string, any> = {
  // Progress chart (Schedule today; later will also be used for Finance/Cost progress)
  kpi_sched_progress_v1: ScheduleThermometer,
  // Schedule Variance / SPI gauge + history (EVM-lite)
  kpi_sched_variance_v1: ScheduleVariance,
  // Finance/Budget Progress chart (cumulative budget vs actual spend)
  kpi_cost_progress_v1: BudgetThermometer,
  // Cost Variance / CPI gauge + history
  kpi_cost_variance_v1: BudgetVariance,
};

const UnknownChartCard = ({
  title,
  catalogKpiGuid,
  displayMode,
}: {
  title: string;
  catalogKpiGuid: string;
  displayMode?: "full" | "mini";
}) => {
  return (
    <div
      style={{
        border: "var(--border-subtle)",
        borderRadius: 12,
        padding: displayMode === "mini" ? 12 : 14,
        background: "var(--bg-active)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--text-secondary)",
            padding: "3px 8px",
            borderRadius: 999,
            border: "var(--border-subtle)",
            background: "var(--bg-hover)",
            whiteSpace: "nowrap",
          }}
          title={catalogKpiGuid}
        >
          Not implemented
        </div>
      </div>
      <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85, lineHeight: 1.35 }}>
        This chart type isn’t implemented in the UI yet.
        <div
          style={{
            marginTop: 6,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: 12,
            opacity: 0.85,
          }}
        >
          catalog_kpi_guid: {catalogKpiGuid}
        </div>
      </div>
    </div>
  );
};

const tabsContent = [
  { label: FIELD_TYPES.SUMMARY },
  { label: FIELD_TYPES.SCHEDULE },
  { label: FIELD_TYPES.FINANCE },
  { label: FIELD_TYPES.MISSION },
  { label: FIELD_TYPES.DATA },
];

const getKpiValueKeys = (label: string) => {
  switch (label) {
    case FIELD_TYPES.SUMMARY:
      return { colorKey: 'summary_indicator_color', trendKey: 'summary_indicator_trend' };
    case FIELD_TYPES.SCHEDULE:
      return { colorKey: 'schedule_indicator_color', trendKey: 'schedule_indicator_trend' };
    case FIELD_TYPES.FINANCE:
      return { colorKey: 'finance_indicator_color', trendKey: 'finance_indicator_trend' };
    case FIELD_TYPES.MISSION:
      return { colorKey: 'mission_indicator_color', trendKey: 'mission_indicator_trend' };
    case FIELD_TYPES.DATA:
      return { colorKey: 'data_reliability_color', trendKey: 'data_reliability_trend' };
    default:
      return { colorKey: 'summary_indicator_color', trendKey: 'summary_indicator_trend' };
  }
};

type Props = {
  payload: any;
  currentUserGuid?: string;
  // 0=Summary, 1=Schedule, 2=Finance, 3=Mission, 4=Data
  selectedTabIndex?: number;
  debugMode?: boolean;
  onProjectConfigChanged?: () => void;
  onTabIndexChange?: (tabIndex: number) => void;
  /** Called when the user presses Back on a GroupProgressPage (restore previous view). */
  onBack?: () => void;
  /** Called when the user clicks a project inside a GroupProgressPage. */
  onSelectProject?: (item: any, tabIndex: number) => void;
  /** Called to navigate back to the group ad-hoc page this project was opened from. */
  onBackToGroup?: () => void;
}

const KpiNavigation = ({
  payload: selectedItem,
  currentUserGuid,
  selectedTabIndex = 0,
  debugMode = false,
  onProjectConfigChanged,
  onTabIndexChange,
  onBack,
  onSelectProject,
  onBackToGroup,
}: Props) => {
  const { t: tKpi } = useAppTranslation('kpi');
  const { t: tPages } = useAppTranslation('pages');
  const { t: tCommon } = useAppTranslation('common');

  /** Localized display name for KPI tab labels. Uses common:kpiCategory keys. */
  const tabDisplayName = (rawLabel: string): string => {
    const key = `kpiCategory.${rawLabel}`;
    const translated = tCommon(key);
    // If tCommon returns the key itself (missing translation), capitalize the raw label.
    return translated !== key ? translated : rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
  };

  const [activeTab, setActiveTab] = useState<number>(0);
  const [progressByDate, setProgressByDate] = useState<number | null>(null);
  const [selectedMissionSubkpiKey, setSelectedMissionSubkpiKey] = useState<string>('');
  const [summaryShowMath, setSummaryShowMath] = useState<boolean>(false);
  const [summaryMode, setSummaryMode] = useState<"all" | "period">("all");

  // Budget subtab: total estimated cost from CostBreakdownPanel, used for funding coverage calc
  const [budgetTotalEstimated, setBudgetTotalEstimated] = useState<number>(0);

  // ScheduleThermometer calculation diagnostics (fed into the Schedule tab debug window)
  const [scheduleCalcDebug, setScheduleCalcDebug] = useState<Record<string, any>>({});

  // For group rows, use the aggregated sub-KPIs (tagged with _project_name/_project_guid).
  // For project rows, use the standard mission_subkpis from the scoring engine.
  const missionSubkpisRaw: any[] = (() => {
    const groupSubs = (selectedItem as any)?.group_mission_subkpis;
    if (Array.isArray(groupSubs) && groupSubs.length > 0) return groupSubs;
    const projSubs = (selectedItem as any)?.mission_subkpis;
    if (Array.isArray(projSubs)) return projSubs;
    return [];
  })();

  // DEV-816 / Skip's bug report (#88, #97): preserve the user-defined
  // order from Project Settings → KPI tab. Previously this re-sorted by
  // weight_used DESCENDING — which caused cards to leap up the list the
  // moment they started scoring (e.g., the first time a Checklist item
  // was checked, that KPI's weight_used went 0 → 100 and it jumped to
  // the top). User-defined order should be authoritative; the engine
  // already returns sub-KPIs in the array order set by App.tsx, which
  // mirrors the missionInstances array (user's saved order).
  const missionSubkpis = missionSubkpisRaw;

  // ── Mission tag filter + group-by-tag ────────────────────────
  const [missionTagFilter, setMissionTagFilter] = useState<string>('');
  const [missionGroupByTag, setMissionGroupByTag] = useState<boolean>(false);

  /** All unique tags across currently visible mission sub-KPIs */
  const missionAllTags = useMemo(() => {
    const set = new Set<string>();
    for (const sk of missionSubkpis) {
      if (Array.isArray(sk?.tags)) {
        for (const t of sk.tags) if (t) set.add(t);
      }
    }
    return [...set].sort();
  }, [missionSubkpis]);

  /**
   * Mission sub-KPIs after tag filter is applied AND HSE-typed sub-KPIs
   * are removed (HSE renders separately as a top-of-tab row, not as
   * regular Mission rows). DEV-822.
   */
  const missionFilteredSubkpis = useMemo(() => {
    const nonHse = missionSubkpis.filter(
      (sk: any) => sk?.mission_type_key !== 'incident_count',
    );
    if (!missionTagFilter) return nonHse;
    return nonHse.filter((sk: any) =>
      Array.isArray(sk?.tags) && sk.tags.includes(missionTagFilter)
    );
  }, [missionSubkpis, missionTagFilter]);

  /**
   * HSE-typed sub-KPIs (mission_type_key='incident_count') extracted from
   * the regular missionSubkpis list. These render as three square cards
   * via HSERow at the top of the Mission tab. DEV-822.
   */
  const hseSubkpis = useMemo(() => {
    const all = missionSubkpis;
    const hse = all.filter(
      (sk: any) => sk?.mission_type_key === 'incident_count',
    );
    return hse;
  }, [missionSubkpis]);

  // Fetch HSE incidents for the current project. Returns empty array
  // gracefully if the API endpoint isn't deployed yet (DEV-826 pending).
  const projectGuidStr = String(
    selectedItem?.project_guid ||
    selectedItem?.project?.project_guid ||
    selectedItem?.project_GUID ||
    selectedItem?.projectGuid ||
    ''
  ).trim();
  const { incidents: hseIncidents } = useHseIncidents(projectGuidStr);

  // DEV-822 / DEV-831: ReportIncidentModal state. The "+ Report" button on
  // each HSE card opens the modal pre-filled with the card's category.
  const [hseReportOpen, setHseReportOpen] = useState(false);
  const [hseReportCategory, setHseReportCategory] = useState<HseCategory>('safety');

  // DEV-822: HSE configuration tray state. The pencil button on each HSE
  // card opens the same MissionKpiWizard the Mission cards use, prefilled
  // with that card's SubKpiInstance. Wizard is already trimmed for
  // incident_count (no irrelevant fields rendered).
  const [hseConfigCard, setHseConfigCard] = useState<HseSubKpi | null>(null);

  // DEV-822 / #128: Edit-incident modal state. Clicking an incident row
  // in the drill-down panel opens this modal with the issue pre-loaded.
  // Reuses the standard PATCH endpoint with the new HSE permission gates.
  const [editingIncidentId, setEditingIncidentId] = useState<string | null>(null);

  /**
   * HSE bundle: per-card scores + worst color + capped_by attribution for
   * the Mission summary pill floor cap (DEV-822 § 10).
   *
   * DEV-839: Prefer the bundle pre-computed in App.tsx (attached to
   * selectedItem.hse_bundle) when available — that path uses the bulk-
   * fetched incidents, so it's the authoritative source and matches what
   * KpiGridTable shows. Fall back to local compute when the upstream
   * bundle isn't present (e.g. group rows or rows from older code paths
   * that haven't adopted the App.tsx attachment yet).
   */
  const hseBundle: HseBundle = useMemo(() => {
    const upstream = (selectedItem as any)?.hse_bundle as HseBundle | null | undefined;
    if (upstream) return upstream;
    const configs = hseSubkpis.map((sk: any) => ({
      sub_kpi_guid: String(sk?.sub_kpi_guid || sk?.subKpiKey || ''),
      config: (sk?.mission_type_config ?? {
        display_name: sk?.label ?? 'HSE',
        hse_category: 'health',
        display_mode: 'streak',
        period: 'quarter',
      }) as IncidentCountConfig,
    }));
    const today = new Date().toISOString().slice(0, 10);
    return computeHseBundle(configs, hseIncidents, today);
  }, [selectedItem, hseSubkpis, hseIncidents]);

  /**
   * DEV-838: Attach `hse_bundle` to a clone of the selected item so any
   * downstream consumer (KpiSummaryHeader, future KPI cells, etc.) can
   * read it through `getMissionDisplayState` / `getSummaryDisplayState`
   * helpers. This is the contract that keeps the floor-cap rule in ONE
   * place (`src/Data/displayKpiState.ts`) instead of being re-implemented
   * per surface — which is how KpiSummaryHeader and KpiGridTable drifted
   * before this refactor.
   *
   * Cheap clone: spread + assign. We don't mutate the source — that would
   * break referential equality for memos elsewhere.
   */
  const itemWithHse = useMemo(() => {
    if (!selectedItem) return selectedItem;
    return { ...selectedItem, hse_bundle: hseBundle };
  }, [selectedItem, hseBundle]);

  const isGroup = selectedItem?.__type === 'group';
  const groupFilterText: string = (selectedItem?.filterText || '').trim();

  // ── Schedule tasks for Summary status section ──────────────
  const [scheduleTasks, setScheduleTasks] = useState<GanttTask[]>([]);

  useEffect(() => {
    if (!selectedItem || isGroup) { setScheduleTasks([]); return; }
    const guid = String(
      (selectedItem as any)?.project_guid ||
      (selectedItem as any)?.project?.project_guid ||
      (selectedItem as any)?.projectGuid || ""
    );
    if (!guid) { setScheduleTasks([]); return; }
    let cancelled = false;
    fetchGanttTasksByProject(guid)
      .then((res) => {
        if (!cancelled) setScheduleTasks(res.map((r, i) => apiResponseToGanttTask(r, i)));
      })
      .catch(() => { if (!cancelled) setScheduleTasks([]); });
    return () => { cancelled = true; };
  }, [selectedItem?.project_guid, isGroup]);

  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);


  // Default the selected mission subKPI for the chart when the project selection changes.
  useEffect(() => {
    if (!selectedItem || isGroup) return;
    if (missionSubkpis.length === 0) {
      setSelectedMissionSubkpiKey('');
      return;
    }
    setSelectedMissionSubkpiKey((prev) => {
      return prev ? prev : String(missionSubkpis[0]?.subKpiKey ?? '');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.project_guid, isGroup, missionSubkpisRaw.length]);
  const titleize = (s: string): string => {
    const t = String(s ?? '').replace(/[_\-]+/g, ' ').trim();
    if (!t) return '';
    return t.replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const safeScore = (v: any): number | null => {
    if (v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const headerTitle = isGroup
    ? `${selectedItem?.groupLabel || 'Group'} groups${groupFilterText ? ` (filtered on '${groupFilterText}')` : ''}`
    : (selectedItem?.project?.project_name || '-');

  const canEdit = !!selectedItem && !isGroup && !!selectedItem?.project;
  const editProject = useMemo(() => {
    // Pass the raw project model into the modal (merged via App at load)
    return canEdit ? selectedItem.project : null;
  }, [canEdit, selectedItem?.project]);

  const groupItems: any[] = isGroup ? ((selectedItem?.__items || selectedItem?.items) || []) : [];

  useEffect(() => {
    if (selectedItem && !isGroup) {
      const progress = getDateProgressPercentage(selectedItem?.project?.start_date, selectedItem?.project?.finish_date);
      setProgressByDate(progress);
    } else {
      setProgressByDate(null);
    }
  }, [selectedItem, isGroup])

  // When a new item is selected from the grid, optionally jump to the requested tab.
  useEffect(() => {
    if (selectedItem) {
      const idx = typeof selectedTabIndex === 'number' ? selectedTabIndex : 0;
      setActiveTab(Math.max(0, Math.min(idx, tabsContent.length - 1)));
    }
  }, [selectedItem, selectedTabIndex]);

  // Reset expandable math blocks when switching projects.
  useEffect(() => {
    setSummaryShowMath(false);
  }, [selectedItem?.project_guid]);

  const onTabSelect = (e: number) => {
    setActiveTab(e);
    try { onTabIndexChange?.(e); } catch {}
  }

  const onSaved = () => {
    if (onProjectConfigChanged) onProjectConfigChanged();
  };

  const DebugTextarea = ({ title, data }: { title: string; data: any }) => (
    <div style={{ paddingInlineStart: '30px', paddingInlineEnd: '18px', marginTop: '10px' }}>
      <div style={{ fontWeight: 700, marginBottom: '6px' }}>{title}</div>
      <textarea
        readOnly
        style={{
          width: '100%',
          height: '260px',
          resize: 'vertical',
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          fontSize: '12px',
          lineHeight: 1.35,
          padding: '10px',
          borderRadius: '8px',
          border: 'var(--border-subtle)',
          background: 'var(--bg-active)',
          color: 'var(--text-primary)',
        }}
        value={JSON.stringify(data, null, 2)}
      />
    </div>
  );


  const TabInfographic = ({ label, item }: { label: string; item: any }) => {
    const mode = String(item?.kpi_mode ?? 'live').toUpperCase();
    const asOf =
      label === FIELD_TYPES.MISSION
        ? (item?.mission_as_of_date || item?.kpi_as_of_date || item?.reporting_date)
        : (item?.kpi_as_of_date || item?.reporting_date || '-');

    const scoreKey =
      label === FIELD_TYPES.SUMMARY ? 'summary_score_0_100'
      : label === FIELD_TYPES.SCHEDULE ? 'schedule_score_0_100'
      : label === FIELD_TYPES.MISSION ? 'mission_score_0_100'
      : label === FIELD_TYPES.DATA ? 'data_reliability_score_0_100'
      : null;

    const scoreVal = scoreKey ? safeScore((item as any)?.[scoreKey]) : null;

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '14px',
        padding: '12px 14px',
        borderRadius: '10px',
        border: 'var(--border-subtle)',
        background: 'var(--bg-hover)',
        marginBottom: '12px',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, fontSize: '16px', textTransform: 'capitalize' }}>{label}</div>
            {/* TODO: Live/Official pill — commented out until kpi_mode logic is fixed.
            <div style={{
              fontSize: '11px',
              letterSpacing: '0.08em',
              padding: '3px 8px',
              borderRadius: '999px',
              border: 'var(--border-subtle)',
              background: 'var(--bg-hover)',
            }}>
              {mode}
            </div>
            */}
            <div style={{ fontSize: '12px', opacity: 0.8 }}>
              as-of {asOf || '-'}
            </div>
          </div>
          <div style={{ fontSize: '12px', opacity: 0.75, marginTop: '4px' }}>
            Score is color-banded: Green ≥ 80, Yellow 60–79, Red &lt; 60. Grey = no data.
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '0 0 auto' }}>
          {scoreKey ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <div style={{ fontSize: '26px', fontWeight: 800, lineHeight: 1 }}>
                {scoreVal === null ? '--' : Math.round(scoreVal)}
              </div>
              <div style={{ opacity: 0.75, fontSize: '12px' }}>/100</div>
            </div>
          ) : (
            <div style={{ fontSize: '12px', opacity: 0.75 }}>No score</div>
          )}
          <div>
            {getKpiIndicator(label, item)}
          </div>
        </div>
      </div>
    );
  };

  // Render

  const Header = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '10px',
      padding: '12px 18px',
      borderBottom: 'var(--border-subtle)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <div style={{
          fontSize: '22px',
          fontWeight: 800,
          color: 'var(--text-primary)',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {headerTitle}
        </div>
        {/* TODO: Live/Official pill — commented out until kpi_mode logic is fixed.
        {!isGroup && (
          <div
            style={{
              fontSize: '11px',
              letterSpacing: '0.08em',
              padding: '4px 9px',
              borderRadius: '999px',
              border: 'var(--border-subtle)',
              background: 'var(--bg-hover)',
              color: 'var(--text-primary)',
              flex: '0 0 auto',
              whiteSpace: 'nowrap',
            }}
            title="KPI reporting mode"
          >
            {String((selectedItem as any)?.kpi_mode ?? 'live').toUpperCase()}
          </div>
        )}
        */}
      </div>
      {canEdit ? (
        <button
          onClick={() => setIsEditOpen(true)}
          title="Edit project"
          style={{
            border: 'var(--border-subtle)',
            background: 'var(--bg-hover)',
            color: 'var(--text-primary)',
            borderRadius: '10px',
            padding: '8px 10px',
            cursor: 'pointer',
            flex: '0 0 auto'
          }}
        >
          ✎
        </button>
      ) : null}
    </div>
  );

  const SummaryExplain = ({ item }: { item: any }) => {
    const weights = (kpiConfig as any)?.summary?.weights ?? {};

    const schedule = safeScore((item as any)?.schedule_score_0_100);
    const finance = safeScore((item as any)?.finance_score_0_100);
    const mission = safeScore((item as any)?.mission_score_0_100);
    const reliability = safeScore((item as any)?.data_reliability_score_0_100);
    const summary = safeScore((item as any)?.summary_score_0_100);

    const parts: Array<{ key: string; label: string; score: number | null; weight: number }> = [
      { key: 'schedule', label: 'Schedule', score: schedule, weight: Number((weights as any)?.schedule ?? 0) },
      { key: 'finance', label: 'Budget', score: finance, weight: Number((weights as any)?.finance ?? 0) },
      { key: 'mission', label: 'Mission', score: mission, weight: Number((weights as any)?.mission ?? 0) },
      { key: 'data_reliability', label: 'Data Reliability', score: reliability, weight: Number((weights as any)?.data_reliability ?? 0) },
    ];

    const usable = parts.filter((p) => p.weight > 0 && p.score !== null && Number.isFinite(p.score as number));
    const weightSum = usable.reduce((a, p) => a + p.weight, 0);
    const preGate = weightSum > 0
      ? usable.reduce((a, p) => a + (p.weight / weightSum) * (p.score as number), 0)
      : null;

    const treatedReliability = reliability === null ? 50 : reliability;
    const gateMult = 0.5 + 0.5 * (treatedReliability / 100);
    const postGate = preGate === null ? null : preGate * gateMult;

    const scoreLine = (v: number | null) => (v === null ? '--' : `${Math.round(v)}`);

    const biggestDriver = (() => {
      if (!usable.length) return null;
      const lowest = [...usable].sort((a, b) => (a.score as number) - (b.score as number))[0];
      return lowest ? lowest.label : null;
    })();

    return (
      <div style={{ paddingInlineStart: '30px', paddingInlineEnd: '18px', marginTop: '-4px', marginBottom: '10px' }}>
        <div style={{ fontWeight: 800, marginBottom: '6px' }}>Summary Insight</div>
        <div style={{ fontSize: '13px', opacity: 0.88, lineHeight: 1.45 }}>
          The Summary score isn't a simple count of green indicators. Each KPI contributes based on its configured weight,
          and the result is adjusted by Data Reliability.
          {biggestDriver ? (
            <span> Biggest driver of the overall score right now: <b>{biggestDriver}</b>.</span>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => setSummaryShowMath((s) => !s)}
          style={{
            marginTop: '8px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            fontSize: '12px',
            textDecoration: 'underline',
          }}
        >
          {summaryShowMath ? 'Hide calculation' : 'Show calculation'}
        </button>

        {summaryShowMath && (
          <div
            style={{
              marginTop: '10px',
              borderRadius: '10px',
              border: 'var(--border-subtle)',
              background: 'var(--bg-active)',
              padding: '10px 12px',
              fontSize: '12px',
              lineHeight: 1.5,
              opacity: 0.92,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '6px' }}>Calculation</div>
            <div style={{ marginBottom: '6px', opacity: 0.9 }}>
              1) Weighted average of available KPI scores (weights normalize if any score is missing).
            </div>
            {usable.length ? (
              <div style={{ paddingInlineStart: '4px' }}>
                {usable.map((p) => (
                  <div key={p.key} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                    <span>
                      {p.label}: {scoreLine(p.score)} × weight {Math.round(p.weight)}%
                    </span>
                    <span style={{ opacity: 0.85 }}>
                      contribution ≈ {Math.round(((p.weight / weightSum) * (p.score as number)) * 10) / 10}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ opacity: 0.85 }}>No KPI scores available to compute a weighted average.</div>
            )}

            <div style={{ marginTop: '8px' }}>
              <div style={{ opacity: 0.9 }}>Pre-gate score: <b>{scoreLine(preGate)}</b></div>
              <div style={{ opacity: 0.9 }}>
                2) Reliability gate multiplier: <b>{Math.round(gateMult * 100) / 100}</b>{' '}
                (based on Data Reliability {reliability === null ? '— treated as 50' : Math.round(reliability)}).
              </div>
              <div style={{ opacity: 0.95 }}>Final Summary score: <b>{summary === null ? scoreLine(postGate) : Math.round(summary)}</b>/100</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Group Progress Page (replaces standard tabs when payload is a group) ───
  if (isGroup && selectedItem) {
    return (
      <GroupProgressPage
        groupPayload={selectedItem}
        onBack={onBack || (() => {})}
        onSelectProject={onSelectProject || (() => {})}
        kpiCategoryLabel={
          activeTab === 2 ? "Budget"
          : activeTab === 3 ? "Mission"
          : activeTab === 4 ? "Data Reliability"
          : activeTab === 0 ? "Summary"
          : "Schedule"
        }
        kpiCategory={
          activeTab === 2 ? "finance"
          : activeTab === 3 ? "mission"
          : activeTab === 4 ? "data"
          : activeTab === 0 ? "summary"
          : "schedule"
        }
      />
    );
  }

  return (
    <>
      {
        selectedItem ? (
          <div className={styles.wrapper}>
            {/* Back-to-group button when project was opened from a group ad-hoc page */}
            {onBackToGroup && (
              <button
                type="button"
                onClick={onBackToGroup}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 14px",
                  background: "var(--bg-hover)",
                  border: "none",
                  borderBottom: "var(--border-subtle)",
                  color: "var(--text-tertiary)",
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--border-subtle)"; (e.currentTarget as HTMLElement).style.color = "var(--text-primary)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-hover)"; (e.currentTarget as HTMLElement).style.color = "var(--text-tertiary)"; }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back to group
              </button>
            )}
            {debugMode ? (
              <div style={{ padding: "0 18px 10px 18px", fontSize: 12, opacity: 0.85 }}>
                Nav tabs: activeTab={activeTab} | selectedTabIndex(prop)={selectedTabIndex}
              </div>
            ) : null}
            <main className={styles.main}>
              {/*
                Hero row replaces the old TabList as the persistent navigation (Option 2).

                DEV-838: We pass `itemWithHse` (selectedItem + hse_bundle) instead
                of selectedItem. KpiSummaryHeader applies the HSE floor cap via
                the shared `getMissionDisplayState` helper — the OLD `hseFloorCap`
                prop is gone. That contract lives in src/Data/displayKpiState.ts
                so KpiGridTable, MissionStatusCard, and any future cell render
                the same color for the same data.
              */}
              <KpiSummaryHeader
                selectedItem={itemWithHse}
                activeTab={activeTab}
                onSelectTab={onTabSelect}
              />
              <Tabs
                className={styles.tabs}
                selectedTabClassName={styles.selected}
                selectedIndex={activeTab}
                onSelect={onTabSelect}
              >
                {/* TabList hidden — react-tabs needs it in the DOM, but the hero row above
                    is the visible navigation. See KpiSummaryHeader. */}
                <TabList className={styles.list} style={{ display: 'none' }}>
                  {
                    tabsContent.map((t, contentIndex) => (
                      <Tab key={`${contentIndex}_${t.label}`} className={styles.tab}>
                        <div className={styles['tab-content']}>
                          <span className={styles['tab-title']}>{tabDisplayName(t.label)}</span>
                          <div className={styles['tab-mark']}>
                            {getKpiIndicator(t.label, selectedItem, 'xl')}
                          </div>
                        </div>
                      </Tab>
                    ))
                  }
                </TabList>
                <div className={styles['tab-panel-wrapper']}>
                  {
                    tabsContent.map((t, i) => (
                      <TabPanel key={`${t.label}_${i}`} className={i === activeTab ? styles['tab-panel'] : ''}>
                        <div className={styles['content-wrapper']}>
                          <div className={styles.content}>
                            <div className={styles['tabBody']}>
                              {isGroup ? (
                                <div className={styles['groupList']}>
                                  {groupItems.length ? (
                                    groupItems.map((p) => {
                                      const keys = getKpiValueKeys(t.label);
                                      const colorVal = p?.[keys.colorKey];
                                      const trendVal = p?.[keys.trendKey];
                                      const name = p?.project?.project_name || '(unnamed)';
                                      return (
                                        <div key={`${name}_${p?.project_guid || ''}`} className={styles['groupListRow']}>
                                          <div className={styles['groupListLeft']}>
                                            <div className={styles['groupListName']}>{name}</div>
                                            <div className={styles['groupListMeta']}>
                                              <span>color: {colorVal}</span>
                                              <span className={styles['dot']}>•</span>
                                              <span>trend: {trendVal}</span>
                                            </div>
                                          </div>
                                          <div className={styles['groupListIcon']}>{getKpiIndicator(t.label, p, 'sm')}</div>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <div className={styles['groupListEmpty']}>No projects in this group.</div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <div>

                                    {/* TabInfographic removed — user requested removal of as-of reporting date blocks */}
                                    {/* Schedule tab: chart only (no "SCHEDULE:" label, no placeholder text). */}
                                    {t.label === FIELD_TYPES.SCHEDULE && (
                                      (() => {
                                        const projectGuid = String(
                                          (selectedItem as any)?.project_guid ||
                                            (selectedItem as any)?.project_GUID ||
                                            (selectedItem as any)?.guid ||
                                            (selectedItem as any)?.projectGuid ||
                                            "",
                                        ).trim();

                                        // Pull the per-project KPI Configuration instances via adapter.
                                        const scheduleInstances: any[] = projectGuid
                                          ? getScheduleInstancesFromStorage(projectGuid) as any[]
                                          : [];

                                        // Backward-compat: older saved instances may use slightly different field names.
                                        const getInstCatalogGuid = (inst: any) =>
                                          String(inst?.catalog_kpi_guid || inst?.kpi_catalog_guid || inst?.kpiCatalogGuid || "").trim();
                                        const getInstInstanceGuid = (inst: any) =>
                                          String(inst?.instance_guid || inst?.instanceGuid || inst?.guid || "").trim();

                                        // If any progress charts exist, migrate legacy per-project schedule progress into the *first* progress instance.
                                        const firstProgressInstanceGuid = String(
                                          getInstInstanceGuid(
                                            scheduleInstances.find((inst: any) => getInstCatalogGuid(inst).toLowerCase() === "kpi_sched_progress_v1"),
                                          ) || "",
                                        ).trim();

                                        // No configured KPI instances → show empty-state prompt.
                                        if (!scheduleInstances.length) {
                                          return (
                                            <div style={{
                                              padding: "28px 20px",
                                              textAlign: "center",
                                              color: "var(--text-secondary)",
                                              fontSize: 13,
                                              lineHeight: 1.5,
                                            }}>
                                              <div style={{ fontWeight: 650, marginBottom: 6, color: "var(--text-primary)" }}>
                                                No Schedule KPIs configured
                                              </div>
                                              <div>
                                                Add sub-KPIs in <b>Project Settings → KPI Configuration</b> to populate this tab.
                                              </div>
                                            </div>
                                          );
                                        }

                                        // --- Timeline progress bar ---
                                        const startDate = String(
                                          (selectedItem as any)?.project?.start_date ||
                                          (selectedItem as any)?.start_date ||
                                          (selectedItem as any)?.project?.project_start_date ||
                                          (selectedItem as any)?.project_start_date ||
                                          ""
                                        ).trim();
                                        const finishDate = String(
                                          (selectedItem as any)?.project?.finish_date ||
                                          (selectedItem as any)?.finish_date ||
                                          (selectedItem as any)?.project?.project_end_date ||
                                          (selectedItem as any)?.project_end_date ||
                                          ""
                                        ).trim();
                                        const schedColorCode = Number(
                                          (selectedItem as any)?.schedule_indicator_color ?? 0
                                        );
                                        const schedColor = bucketToColor(indicatorBucket(schedColorCode));

                                        // Actual completion % from the first progress instance's series data.
                                        // This matches what ScheduleThermometer displays (e.g. 71.1%).
                                        const timelineProgress = (() => {
                                          if (!projectGuid || !firstProgressInstanceGuid) {
                                            return getDateProgressPercentage(startDate, finishDate) ?? 0;
                                          }
                                          const cd = getInstanceChartDataFromStorage(projectGuid, firstProgressInstanceGuid);
                                          const pts = cd?.series;
                                          if (!pts || !pts.length) {
                                            return getDateProgressPercentage(startDate, finishDate) ?? 0;
                                          }
                                          // Find most recent row with a non-null actual_complete.
                                          const today = new Date().toISOString().slice(0, 10);
                                          const pastActuals = pts
                                            .filter((r: any) => r.reporting_date <= today && r.actual_complete != null)
                                            .sort((a: any, b: any) => String(b.reporting_date).localeCompare(String(a.reporting_date)));
                                          if (!pastActuals.length) {
                                            return getDateProgressPercentage(startDate, finishDate) ?? 0;
                                          }
                                          const raw = Number(pastActuals[0].actual_complete);
                                          if (!Number.isFinite(raw)) return 0;
                                          // Auto-detect 0-1 vs 0-100 scale
                                          const maxV = pts.reduce((mx: number, r: any) => {
                                            const vals = [r.baseline_complete, r.actual_complete, r.forecast_complete]
                                              .map(Number).filter(Number.isFinite);
                                            return Math.max(mx, ...vals);
                                          }, 0);
                                          const scale = maxV > 1.5 ? 100 : 1;
                                          const pct = scale === 100 ? raw : raw * 100;
                                          return Math.round(pct * 10) / 10;
                                        })();

                                        const formatDate = (iso: string) => {
                                          if (!iso) return "TBD";
                                          return formatDateMedium(iso);
                                        };

                                        return (
                                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                            {/* Timeline progress bar */}
                                            <div style={{
                                              padding: "10px 14px 12px",
                                              borderRadius: 10,
                                              background: "var(--bg-active)",
                                              border: "var(--border-subtle)",
                                              marginBottom: 4,
                                            }}>
                                              <div style={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                letterSpacing: "0.06em",
                                                color: "var(--text-tertiary)",
                                                marginBottom: 7,
                                                textTransform: "uppercase",
                                              }}>{tPages('projectDetail.core.workCompleted')}</div>
                                              <div style={{
                                                height: 10,
                                                borderRadius: 5,
                                                background: "var(--border-subtle)",
                                                overflow: "hidden",
                                                position: "relative",
                                              }}>
                                                <div style={{
                                                  height: "100%",
                                                  borderRadius: 5,
                                                  width: `${timelineProgress}%`,
                                                  background: schedColor,
                                                  transition: "width 300ms ease",
                                                }} />
                                              </div>
                                              <div style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                fontSize: 11,
                                                color: "var(--text-tertiary)",
                                                marginTop: 6,
                                              }}>
                                                <span><b style={{ fontWeight: 600, color: "var(--text-secondary)" }}>Start:</b> {formatDate(startDate)}</span>
                                                <span style={{ fontWeight: 600, color: schedColor }}>{timelineProgress}%</span>
                                                <span><b style={{ fontWeight: 600, color: "var(--text-secondary)" }}>Finish:</b> {formatDate(finishDate)}</span>
                                              </div>
                                            </div>

                                            {scheduleInstances.map((inst: any, idx: number) => {
                                              const catalogKpiGuid = getInstCatalogGuid(inst);
                                              const instanceGuid = getInstInstanceGuid(inst);
                                              const displayName = String(inst?.display_name || "").trim() || "Chart";
                                              // Default density: first chart full, others mini.
                                              const rawMode = String(inst?.display_mode || inst?.displayMode || "").toLowerCase();
                                              const displayMode: "full" | "mini" =
                                                rawMode === "mini"
                                                  ? "mini"
                                                  : rawMode === "full"
                                                    ? "full"
                                                    : idx === 0
                                                      ? "full"
                                                      : "mini";

                                              // Defensive: lowercase lookup to handle any legacy uppercased catalog GUIDs
                                              const catalogKeyLower = catalogKpiGuid.toLowerCase();
                                              const ChartCmp = (KPI_CHART_REGISTRY as any)[catalogKeyLower];
                                              if (!ChartCmp) {
                                                return (
                                                  <UnknownChartCard
                                                    key={instanceGuid || `${catalogKpiGuid}_${idx}`}
                                                    title={displayName}
                                                    catalogKpiGuid={catalogKpiGuid || "(missing catalog_kpi_guid)"}
                                                    displayMode={displayMode}
                                                  />
                                                );
                                              }

                                              // Special-case progress chart: per-instance dataset + legacy migration to the first progress instance only.
                                              if (catalogKeyLower === "kpi_sched_progress_v1") {
                                                const shouldMigrateLegacy = !!firstProgressInstanceGuid && instanceGuid === firstProgressInstanceGuid;
                                                // Check if this instance opts into SPI via template switch.
                                                const tmplType = String((inst as any)?.schedule_template_type || "").trim();
                                                const spiEnabled = tmplType === "progress_spi";

                                                // Pre-fetch chart data via adapter for this instance.
                                                const chartData = projectGuid
                                                  ? getInstanceChartDataFromStorage(projectGuid, instanceGuid || undefined)
                                                  : undefined;

                                                // Only show the SPI section when SPI is enabled AND
                                                // the baseline has reached the early-maturity guard threshold
                                                // on at least one past reporting date. Until then, SPI ratios
                                                // are meaningless and showing an empty red chart is misleading.
                                                const showSpiBelow = spiEnabled && (() => {
                                                  if (!chartData?.series?.length) return false;
                                                  const guard = typeof (inst as any)?.spi_min_baseline_pct === "number"
                                                    ? (inst as any).spi_min_baseline_pct
                                                    : 5;
                                                  const today = new Date().toISOString().slice(0, 10);
                                                  return chartData.series.some((pt: any) => {
                                                    const d = String(pt.reporting_date || "");
                                                    if (!d || d > today) return false;
                                                    const b = Number(pt.baseline_complete);
                                                    const a = pt.actual_complete;
                                                    return Number.isFinite(b) && b >= guard && a !== null && a !== undefined;
                                                  });
                                                })();

                                                return (
                                                  <React.Fragment key={instanceGuid || `${catalogKpiGuid}_${idx}`}>
                                                    {showSpiBelow ? (
                                                      /* Combined Progress + SPI container: single bounding box with a divider. */
                                                      <div className={styles.scheduleChartWrap}>
                                                        <ScheduleThermometer
                                                          projectGUID={projectGuid}
                                                          currentUserGuid={currentUserGuid}
                                                          instanceGuid={instanceGuid}
                                                          title={displayName}
                                                          displayMode={displayMode}
                                                          allowSeed={false}
                                                          migrateLegacyFromProject={shouldMigrateLegacy}
                                                          prefetchedSeries={chartData?.series}
                                                          prefetchedCurveMeta={chartData?.curveMeta}
                                                          debugMode={debugMode}
                                                          apiProjectRecord={selectedItem}
                                                          ganttTasks={scheduleTasks}
                                                          embedded
                                                          onDebugUpdate={(d: any) => {
                                                            const k = String(d?.instance_guid || instanceGuid || d?.title || `${idx}`).trim() || `${idx}`;
                                                            setScheduleCalcDebug((prev) => ({ ...prev, [k]: d }));
                                                          }}
                                                        />
                                                        {/* Divider between Progress and SPI */}
                                                        <div
                                                          style={{
                                                            height: 1,
                                                            background: "var(--border-strong)",
                                                            margin: "18px 0",
                                                            position: "relative",
                                                            zIndex: 2,
                                                            flexShrink: 0,
                                                          }}
                                                        />
                                                        <ScheduleVariance
                                                          projectGUID={projectGuid}
                                                          currentUserGuid={currentUserGuid}
                                                          instanceGuid={instanceGuid}
                                                          title={"Schedule Variance (SPI)"}
                                                          displayMode={"full"}
                                                          prefetchedSeries={chartData?.series}
                                                          embedded
                                                          spiMinBaselinePct={
                                                            typeof (inst as any)?.spi_min_baseline_pct === "number"
                                                              ? (inst as any).spi_min_baseline_pct
                                                              : undefined
                                                          }
                                                        />
                                                      </div>
                                                    ) : (
                                                      /* Progress-only: standard standalone wrapper. */
                                                      <ScheduleThermometer
                                                        projectGUID={projectGuid}
                                                        currentUserGuid={currentUserGuid}
                                                        instanceGuid={instanceGuid}
                                                        title={displayName}
                                                        displayMode={displayMode}
                                                        allowSeed={false}
                                                        migrateLegacyFromProject={shouldMigrateLegacy}
                                                        prefetchedSeries={chartData?.series}
                                                        prefetchedCurveMeta={chartData?.curveMeta}
                                                        debugMode={debugMode}
                                                        apiProjectRecord={selectedItem}
                                                        ganttTasks={scheduleTasks}
                                                        onDebugUpdate={(d: any) => {
                                                          const k = String(d?.instance_guid || instanceGuid || d?.title || `${idx}`).trim() || `${idx}`;
                                                          setScheduleCalcDebug((prev) => ({ ...prev, [k]: d }));
                                                        }}
                                                      />
                                                    )}
                                                  </React.Fragment>
                                                );
                                              }

                                              // Default: render any other registered chart (future chart types)
                                              return (
                                                <ChartCmp
                                                  key={instanceGuid || `${catalogKpiGuid}_${idx}`}
                                                  projectGUID={projectGuid}
                                                  currentUserGuid={currentUserGuid}
                                                  instanceGuid={instanceGuid}
                                                  title={displayName}
                                                  displayMode={displayMode}
                                                />
                                              );
                                            })}

                                            {/* Recently Completed + Upcoming Tasks (below curves) */}
                                            <ScheduleStatusRow tasks={scheduleTasks} />

                                            {/* Earned Schedule panel — time-based EVM for schedule performance */}
                                            {(() => {
                                              const pStart = String(
                                                (selectedItem as any)?.project?.start_date ||
                                                (selectedItem as any)?.start_date ||
                                                (selectedItem as any)?.project?.project_start_date ||
                                                (selectedItem as any)?.project_start_date || ""
                                              ).trim() || null;
                                              const pEnd = String(
                                                (selectedItem as any)?.project?.finish_date ||
                                                (selectedItem as any)?.finish_date ||
                                                (selectedItem as any)?.project?.project_end_date ||
                                                (selectedItem as any)?.project_end_date || ""
                                              ).trim() || null;
                                              return (
                                                <EarnedSchedulePanel
                                                  projectGuid={projectGuid}
                                                  scheduleTasks={scheduleTasks}
                                                  projectStart={pStart}
                                                  projectEnd={pEnd}
                                                  debugMode={debugMode}
                                                />
                                              );
                                            })()}
                                          </div>
                                        );
                                      })()
                                    )}



                                    {/* Mission tab: executive summary + SubKPI placeholders */}
                                    {t.label === FIELD_TYPES.MISSION && (() => {
                                      return true;
                                    })() && (
                                      <div className={styles.missionWrap}>
                                        {/* DEV-822: HSE row — three square Health/Safety/Environment
                                            cards. Always renders ABOVE the regular Mission rows.
                                            Renders nothing when no HSE sub-KPIs are configured for
                                            this project (until default-provisioning lands). */}
                                        <HSERow
                                          bundle={hseBundle}
                                          canConfigure={!isGroup && canAdminProject(
                                            String(currentUserGuid ?? ''),
                                            String(selectedItem?.project_guid ?? '')
                                          )}
                                          onConfigureCard={(card) => setHseConfigCard(card)}
                                          onReportIncident={(category) => {
                                            setHseReportCategory(category);
                                            setHseReportOpen(true);
                                          }}
                                          renderDrillDown={(card, onClose) => (
                                            <IncidentDrillDown
                                              card={card}
                                              projectGuid={projectGuidStr}
                                              onClose={onClose}
                                              onViewAll={(_cat) => {
                                                // TODO DEV-832: navigate to Risks & Issues tab
                                                // pre-filtered to HSE for this category. For now
                                                // just close — at the >10 threshold this is a
                                                // soft fallback.
                                                onClose();
                                              }}
                                              onIncidentClick={(issueId) => {
                                                // Opens EditIncidentModal pre-loaded with this
                                                // incident. Drill-down stays open underneath.
                                                setEditingIncidentId(issueId);
                                              }}
                                            />
                                          )}
                                        />
                                        {/* Tag filter bar */}
                                        {missionAllTags.length > 0 && (
                                          <div className={styles.tagFilterBar}>
                                            <button
                                              className={`${styles.tagFilterBtn} ${!missionTagFilter ? styles.tagFilterBtnActive : ''}`}
                                              onClick={() => { setMissionTagFilter(''); setMissionGroupByTag(false); }}
                                            >
                                              {tKpi('mission.allTags') || 'All'}
                                            </button>
                                            {missionAllTags.map((tag) => (
                                              <button
                                                key={tag}
                                                className={`${styles.tagFilterBtn} ${missionTagFilter === tag ? styles.tagFilterBtnActive : ''}`}
                                                onClick={() => setMissionTagFilter(missionTagFilter === tag ? '' : tag)}
                                              >
                                                {tag}
                                              </button>
                                            ))}
                                            <span className={styles.tagFilterSep} />
                                            <button
                                              className={`${styles.tagFilterBtn} ${missionGroupByTag ? styles.tagFilterBtnActive : ''}`}
                                              onClick={() => setMissionGroupByTag((v) => !v)}
                                              title={tKpi('mission.groupByTag') || 'Group by tag'}
                                            >
                                              {tKpi('mission.groupByTag') || 'Group by tag'}
                                            </button>
                                          </div>
                                        )}
                                        {missionSubkpis.length === 0 ? (
                                          <div className={styles.subkpiEmpty}>
                                            {tKpi('mission.emptyState') || 'No Mission KPIs configured. Add them in Project Settings → KPI Configuration → Mission.'}
                                          </div>
                                        ) : isGroup ? (
                                          /* ── Group view: sub-KPIs grouped by project ── */
                                          <div className={styles.subkpiList}>
                                            {(() => {
                                              // Group sub-KPIs by project
                                              const byProject: Record<string, { name: string; guid: string; subs: any[] }> = {};
                                              for (const sk of missionFilteredSubkpis) {
                                                const pName = sk?._project_name ?? 'Unknown';
                                                const pGuid = sk?._project_guid ?? '';
                                                const key = pGuid || pName;
                                                if (!byProject[key]) byProject[key] = { name: pName, guid: pGuid, subs: [] };
                                                byProject[key].subs.push(sk);
                                              }
                                              return Object.values(byProject).map((group) => (
                                                <div key={group.guid || group.name} style={{ marginBlockEnd: 12 }}>
                                                  <div style={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: 'var(--text-secondary)',
                                                    paddingBlockEnd: 4,
                                                    paddingInlineStart: 4,
                                                    borderBlockEnd: '1px solid var(--border-subtle)',
                                                    marginBlockEnd: 6,
                                                  }}>
                                                    {group.name}
                                                  </div>
                                                  {group.subs.map((sk, idx) => {
                                                    const indicatorItem = {
                                                      mission_indicator_color: sk?.indicator_color_value,
                                                      mission_indicator_trend: sk?.indicator_trend_value,
                                                      project: { project_name: group.name },
                                                    };
                                                    return (
                                                      <MissionSubKpiCard
                                                        key={`${group.guid}-${String(sk?.subKpiKey ?? idx)}`}
                                                        subKpi={sk}
                                                        projectGuid={group.guid}
                                                        rawPoints={[]}
                                                        projectTargets={undefined}
                                                        defaultExpanded={false}
                                                        canEdit={false}
                                                        renderIndicator={() => getKpiIndicator(FIELD_TYPES.MISSION, indicatorItem, 'sm')}
                                                        onDataChanged={onProjectConfigChanged}
                                                      />
                                                    );
                                                  })}
                                                </div>
                                              ));
                                            })()}
                                          </div>
                                        ) : missionGroupByTag && missionAllTags.length > 0 ? (
                                          /* ── Group-by-tag view ── */
                                          <div className={styles.subkpiList}>
                                            {(() => {
                                              const byTag: Record<string, any[]> = {};
                                              const untagged: any[] = [];
                                              for (const sk of missionFilteredSubkpis) {
                                                const tags: string[] = Array.isArray(sk?.tags) ? sk.tags : [];
                                                if (tags.length === 0) {
                                                  untagged.push(sk);
                                                } else {
                                                  for (const tag of tags) {
                                                    (byTag[tag] ||= []).push(sk);
                                                  }
                                                }
                                              }
                                              const tagGroups = Object.entries(byTag).sort(([a], [b]) => a.localeCompare(b));
                                              if (untagged.length > 0) tagGroups.push(['Untagged', untagged]);

                                              return tagGroups.map(([tag, sks]) => (
                                                <div key={tag} style={{ marginBlockEnd: 12 }}>
                                                  <div style={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: 'var(--accent-primary)',
                                                    paddingBlockEnd: 4,
                                                    paddingInlineStart: 4,
                                                    borderBlockEnd: '1px solid var(--border-subtle)',
                                                    marginBlockEnd: 6,
                                                  }}>
                                                    {tag}
                                                  </div>
                                                  {sks.map((sk: any, idx: number) => {
                                                    const indicatorItem = {
                                                      mission_indicator_color: sk?.indicator_color_value,
                                                      mission_indicator_trend: sk?.indicator_trend_value,
                                                      project: selectedItem?.project,
                                                    };
                                                    return (
                                                      <MissionSubKpiCard
                                                        key={`${tag}-${String(sk?.subKpiKey ?? idx)}`}
                                                        subKpi={sk}
                                                        projectGuid={String(selectedItem?.project_guid ?? '')}
                                                        rawPoints={(selectedItem as any)?.mission_raw_points ?? []}
                                                        projectTargets={(selectedItem as any)?.mission_targets}
                                                        defaultExpanded={false}
                                                        canEdit={canEditProjectContent(
                                                          String(currentUserGuid ?? ''),
                                                          String(selectedItem?.project_guid ?? '')
                                                        )}
                                                        canConfigure={!isGroup && canAdminProject(
                                                          String(currentUserGuid ?? ''),
                                                          String(selectedItem?.project_guid ?? '')
                                                        )}
                                                        renderIndicator={() => getKpiIndicator(FIELD_TYPES.MISSION, indicatorItem, 'sm')}
                                                        onDataChanged={onProjectConfigChanged}
                                                      />
                                                    );
                                                  })}
                                                </div>
                                              ));
                                            })()}
                                          </div>
                                        ) : (
                                          <div className={styles.subkpiList}>
                                            {missionFilteredSubkpis.map((sk, idx) => {
                                              const indicatorItem = {
                                                mission_indicator_color: sk?.indicator_color_value,
                                                mission_indicator_trend: sk?.indicator_trend_value,
                                                project: selectedItem?.project,
                                              };
                                              return (
                                                <MissionSubKpiCard
                                                  key={String(sk?.subKpiKey ?? idx)}
                                                  subKpi={sk}
                                                  projectGuid={String(selectedItem?.project_guid ?? '')}
                                                  rawPoints={(selectedItem as any)?.mission_raw_points ?? []}
                                                  projectTargets={(selectedItem as any)?.mission_targets}
                                                  defaultExpanded={missionFilteredSubkpis.length === 1}
                                                  canEdit={!isGroup && canEditProjectContent(
                                                    String(currentUserGuid ?? ''),
                                                    String(selectedItem?.project_guid ?? '')
                                                  )}
                                                  canConfigure={!isGroup && canAdminProject(
                                                    String(currentUserGuid ?? ''),
                                                    String(selectedItem?.project_guid ?? '')
                                                  )}
                                                  renderIndicator={() => getKpiIndicator(FIELD_TYPES.MISSION, indicatorItem, 'sm')}
                                                  onDataChanged={onProjectConfigChanged}
                                                />
                                              );
                                            })}
                                          </div>
                                        )}

{/* Mission tab: debug view (only when Debug Mode is enabled) */}
                                        {debugMode && (
                                          <>
                                            <DebugTextarea
                                              title="Mission debug"
                                              data={{
                                                project_guid: selectedItem?.project_guid,
                                                kpi_mode: (selectedItem as any)?.kpi_mode,
                                                kpi_as_of_date: (selectedItem as any)?.kpi_as_of_date,
                                                mission_indicator_color: selectedItem?.mission_indicator_color,
                                                mission_indicator_trend: selectedItem?.mission_indicator_trend,
                                                mission_score_0_100: (selectedItem as any)?.mission_score_0_100,
                                                mission_as_of_date: (selectedItem as any)?.mission_as_of_date,
                                                mission_explain: (selectedItem as any)?.mission_explain,
                                                mission_subkpis: (selectedItem as any)?.mission_subkpis,
                                                mission_points_count: (selectedItem as any)?.mission_points_count,
                                                mission_points_sample: (selectedItem as any)?.mission_points_sample,
                                                mission_targets: (selectedItem as any)?.mission_targets,
                                                mission_config_subkpis: (kpiConfig as any)?.mission?.subkpis ?? null,
                                              }}
                                            />
                                            <div style={{ paddingInlineStart: '30px', paddingInlineEnd: '18px', marginTop: '8px', opacity: 0.85 }}>
                                              Tip: if <b>mission_points_count</b> is 0, the engine is not receiving points for this project.
                                              If <b>mission_targets</b> is null/empty, the GUID or subKpiKey doesn’t match.
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    )}

                                    {/* Finance tab: debug */}
                                    {t.label === FIELD_TYPES.FINANCE && debugMode && (
                                      <DebugTextarea
                                        title="Finance debug"
                                        data={{
                                          project_guid: selectedItem?.project_guid,
                                          kpi_mode: (selectedItem as any)?.kpi_mode,
                                          kpi_as_of_date: (selectedItem as any)?.kpi_as_of_date,
                                          finance_indicator_color: (selectedItem as any)?.finance_indicator_color,
                                          finance_indicator_trend: (selectedItem as any)?.finance_indicator_trend,
                                          note: 'Finance KPI is currently using existing indicator values (engine pass-through).',
                                        }}
                                      />
                                    )}

                                    {t.label === FIELD_TYPES.FINANCE && (
                                      (() => {
                                        const projectGuid = String(
                                          (selectedItem as any)?.project_guid ||
                                            (selectedItem as any)?.project_GUID ||
                                            (selectedItem as any)?.guid ||
                                            (selectedItem as any)?.projectGuid ||
                                            "",
                                        ).trim();

                                        const budgetInstances: any[] = projectGuid
                                          ? getFinanceInstancesFromStorage(projectGuid) as any[]
                                          : [];

                                        const getInstCatalogGuid = (inst: any) =>
                                          String(inst?.catalog_kpi_guid || inst?.kpi_catalog_guid || inst?.kpiCatalogGuid || "").trim();
                                        const getInstInstanceGuid = (inst: any) =>
                                          String(inst?.instance_guid || inst?.instanceGuid || inst?.guid || "").trim();

                                        const firstProgressInstanceGuid = String(
                                          getInstInstanceGuid(
                                            budgetInstances.find((inst: any) => getInstCatalogGuid(inst).toLowerCase() === "kpi_cost_progress_v1"),
                                          ) || "",
                                        ).trim();

                                        if (!budgetInstances.length) {
                                          return (
                                            <div style={{
                                              padding: "28px 20px",
                                              textAlign: "center",
                                              color: "var(--text-secondary)",
                                              fontSize: 13,
                                              lineHeight: 1.5,
                                            }}>
                                              <div style={{ fontWeight: 650, marginBottom: 6, color: "var(--text-primary)" }}>
                                                No Finance/Budget KPIs configured
                                              </div>
                                              <div>
                                                Add sub-KPIs in <b>Project Settings → KPI Configuration</b> to populate this tab.
                                              </div>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                            {/* Budget overview bar (Tier 1) */}
                                            <BudgetOverviewBar
                                              projectGuid={projectGuid}
                                              selectedItem={selectedItem}
                                            />

                                            {budgetInstances.map((inst: any, idx: number) => {
                                              const catalogKpiGuid = getInstCatalogGuid(inst);
                                              const instanceGuid = getInstInstanceGuid(inst);
                                              const displayName = String(inst?.display_name || "").trim() || "Chart";
                                              const rawMode = String(inst?.display_mode || inst?.displayMode || "").toLowerCase();
                                              const displayMode: "full" | "mini" =
                                                rawMode === "mini"
                                                  ? "mini"
                                                  : rawMode === "full"
                                                    ? "full"
                                                    : idx === 0
                                                      ? "full"
                                                      : "mini";

                                              // Defensive: lowercase lookup to handle any legacy uppercased catalog GUIDs
                                              const catalogKeyLower = catalogKpiGuid.toLowerCase();
                                              const ChartCmp = (KPI_CHART_REGISTRY as any)[catalogKeyLower];
                                              if (!ChartCmp) {
                                                return (
                                                  <UnknownChartCard
                                                    key={instanceGuid || `${catalogKpiGuid}_${idx}`}
                                                    title={displayName}
                                                    catalogKpiGuid={catalogKpiGuid || "(missing catalog_kpi_guid)"}
                                                    displayMode={displayMode}
                                                  />
                                                );
                                              }

                                              // Special-case: Budget Progress with CPI (mirrors Progress + SPI pattern)
                                              if (catalogKeyLower === "kpi_cost_progress_v1") {
                                                const chartData = projectGuid
                                                  ? getFinanceInstanceChartDataFromStorage(projectGuid, instanceGuid || undefined)
                                                  : undefined;

                                                const tmplType = String((inst as any)?.budget_template_type || "").trim();
                                                const cpiEnabled = tmplType === "progress_cpi";

                                                // Only show CPI section when enabled and data has matured
                                                const showCpiBelow = cpiEnabled && (() => {
                                                  if (!chartData?.series?.length) return false;
                                                  const guard = typeof (inst as any)?.cpi_min_baseline_pct === "number"
                                                    ? (inst as any).cpi_min_baseline_pct
                                                    : 5;
                                                  const today = new Date().toISOString().slice(0, 10);
                                                  return chartData.series.some((pt: any) => {
                                                    const d = String(pt.reporting_date || "");
                                                    if (!d || d > today) return false;
                                                    const b = Number(pt.baseline_budget);
                                                    const a = pt.actual_spend;
                                                    return Number.isFinite(b) && b >= guard && a !== null && a !== undefined;
                                                  });
                                                })();

                                                return (
                                                  <React.Fragment key={instanceGuid || `${catalogKpiGuid}_${idx}`}>
                                                    {showCpiBelow ? (
                                                      <div className={styles.scheduleChartWrap}>
                                                        <BudgetThermometer
                                                          projectGUID={projectGuid}
                                                          currentUserGuid={currentUserGuid}
                                                          instanceGuid={instanceGuid}
                                                          title={displayName}
                                                          displayMode={displayMode}
                                                          prefetchedSeries={chartData?.series}
                                                          prefetchedCurveMeta={chartData?.curveMeta}
                                                          apiProjectRecord={selectedItem}
                                                          ganttTasks={scheduleTasks}
                                                          embedded
                                                        />
                                                        {/* Divider between Finance and CPI */}
                                                        <div
                                                          style={{
                                                            height: 1,
                                                            background: "var(--border-strong)",
                                                            margin: "18px 0",
                                                            position: "relative",
                                                            zIndex: 2,
                                                            flexShrink: 0,
                                                          }}
                                                        />
                                                        <BudgetVariance
                                                          projectGUID={projectGuid}
                                                          currentUserGuid={currentUserGuid}
                                                          instanceGuid={instanceGuid}
                                                          title={"Cost Variance (CPI)"}
                                                          displayMode={"full"}
                                                          prefetchedSeries={chartData?.series}
                                                          embedded
                                                          cpiMinBaselinePct={
                                                            typeof (inst as any)?.cpi_min_baseline_pct === "number"
                                                              ? (inst as any).cpi_min_baseline_pct
                                                              : undefined
                                                          }
                                                        />
                                                      </div>
                                                    ) : (
                                                      <BudgetThermometer
                                                        projectGUID={projectGuid}
                                                        currentUserGuid={currentUserGuid}
                                                        instanceGuid={instanceGuid}
                                                        title={displayName}
                                                        displayMode={displayMode}
                                                        prefetchedSeries={chartData?.series}
                                                        prefetchedCurveMeta={chartData?.curveMeta}
                                                        apiProjectRecord={selectedItem}
                                                        ganttTasks={scheduleTasks}
                                                      />
                                                    )}
                                                  </React.Fragment>
                                                );
                                              }

                                              // Default: render any other registered finance chart
                                              return (
                                                <ChartCmp
                                                  key={instanceGuid || `${catalogKpiGuid}_${idx}`}
                                                  projectGUID={projectGuid}
                                                  currentUserGuid={currentUserGuid}
                                                  instanceGuid={instanceGuid}
                                                  title={displayName}
                                                  displayMode={displayMode}
                                                />
                                              );
                                            })}

                                            {/* Cost + Funding breakdown (side-by-side on wide, stacked on narrow) */}
                                            <div className={styles.budgetBreakdownRow}>
                                              <CostBreakdownPanel
                                                projectGuid={projectGuid}
                                                budgetCurrency={(selectedItem as any)?.budget_currency || "USD"}
                                                debugMode={debugMode}
                                                onTotalEstimated={setBudgetTotalEstimated}
                                              />
                                              <FundingBreakdownPanel
                                                projectGuid={projectGuid}
                                                budgetCurrency={(selectedItem as any)?.budget_currency || "USD"}
                                                totalEstimatedCost={budgetTotalEstimated}
                                                debugMode={debugMode}
                                              />
                                            </div>

                                            {/* EVM-Lite — only renders when cost items are linked to Gantt tasks */}
                                            <EvmLitePanel
                                              projectGuid={projectGuid}
                                              budgetCurrency={(selectedItem as any)?.budget_currency || "USD"}
                                              scheduleTasks={scheduleTasks}
                                              debugMode={debugMode}
                                            />
                                          </div>
                                        );
                                      })()
                                    )}

                                    {/* Data Reliability tab: debug */}
                                    {t.label === FIELD_TYPES.DATA && debugMode && (
                                      <DebugTextarea
                                        title="Data reliability debug"
                                        data={{
                                          project_guid: selectedItem?.project_guid,
                                          kpi_mode: (selectedItem as any)?.kpi_mode,
                                          kpi_as_of_date: (selectedItem as any)?.kpi_as_of_date,
                                          data_reliability_color: (selectedItem as any)?.data_reliability_color,
                                          data_reliability_trend: (selectedItem as any)?.data_reliability_trend,
                                          data_reliability_score_0_100: (selectedItem as any)?.data_reliability_score_0_100,
                                          data_reliability_as_of_date: (selectedItem as any)?.data_reliability_as_of_date,
                                          // DEV-851 (PR2): engine v2 composites — Now (lagging) and Outlook (leading).
                                          // Outlook is always applicable=false in v1 until the backend feeds
                                          // (digest acks, RACI counts, preflight, phase summary) are wired.
                                          data_reliability_now: (selectedItem as any)?.data_reliability_now,
                                          data_reliability_outlook: (selectedItem as any)?.data_reliability_outlook,
                                          _isCompletedProject: (selectedItem as any)?._isCompletedProject,
                                          data_reliability_config: (kpiConfig as any)?.data_reliability ?? null,
                                        }}
                                      />
                                    )}

                                    {t.label === FIELD_TYPES.DATA && (
                                      <DataReliabilityTab
                                        selectedItem={selectedItem}
                                        kpiConfig={kpiConfig}
                                        debugMode={debugMode}
                                      />
                                    )}

                                    {/* Schedule tab: debug (thermometer remains visible always) */}
                                    {t.label === FIELD_TYPES.SCHEDULE && debugMode && (
                                      <DebugTextarea
                                        title="Schedule debug"
                                        data={{
                                          project_guid: selectedItem?.project_guid,
                                          kpi_mode: (selectedItem as any)?.kpi_mode,
                                          kpi_as_of_date: (selectedItem as any)?.kpi_as_of_date,
                                          schedule_indicator_color: (selectedItem as any)?.schedule_indicator_color,
                                          schedule_indicator_trend: (selectedItem as any)?.schedule_indicator_trend,
                                          schedule_score_0_100: (selectedItem as any)?.schedule_score_0_100,
                                          schedule_as_of_date: (selectedItem as any)?.schedule_as_of_date,
                                          schedule_explain: (selectedItem as any)?.schedule_explain,
                                          schedule_config: (kpiConfig as any)?.schedule ?? null,

                                          // Per-instance ScheduleThermometer calculation inputs/outputs
                                          schedule_progress_calcs: scheduleCalcDebug,
                                        }}
                                      />
                                    )}

                                    {/* Summary tab — layout order:
                                         1. Project Timeline (progress bar)
                                         2. Phases (E5 ribbon)
                                         3. Narrative (weekly update)
                                         4. Milestones + Mission Status (2-up)
                                         5. Risks & Issues
                                    */}
                                    {t.label === FIELD_TYPES.SUMMARY && (
                                      <div className={styles.summaryStack}>
                                        {/* ── 1. Project Timeline (factual duration bar, not a KPI) ── */}
                                        {(() => {
                                          const startDate = String(
                                            (selectedItem as any)?.project?.start_date ||
                                            (selectedItem as any)?.start_date ||
                                            (selectedItem as any)?.project?.project_start_date ||
                                            (selectedItem as any)?.project_start_date ||
                                            ""
                                          ).trim();
                                          const finishDate = String(
                                            (selectedItem as any)?.project?.finish_date ||
                                            (selectedItem as any)?.finish_date ||
                                            (selectedItem as any)?.project?.project_end_date ||
                                            (selectedItem as any)?.project_end_date ||
                                            ""
                                          ).trim();

                                          const tlProgress = getDateProgressPercentage(startDate, finishDate) ?? 0;
                                          const fmtDate = (iso: string) => iso ? formatDateMedium(iso) : "TBD";

                                          const schedColorCode = Number(
                                            (selectedItem as any)?.schedule_indicator_color ?? 0
                                          );
                                          const tlBarColor = tlProgress >= 100
                                            ? 'var(--info-text)'
                                            : bucketToColor(indicatorBucket(schedColorCode));

                                          return (
                                            <div style={{
                                              padding: "10px 14px 12px",
                                              borderRadius: 10,
                                              background: "var(--bg-active)",
                                              border: "var(--border-subtle)",
                                            }}>
                                              <div style={{
                                                fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                                                color: "var(--text-tertiary)", marginBottom: 7, textTransform: "uppercase",
                                              }}>Project Timeline</div>
                                              <div style={{
                                                height: 10, borderRadius: 5,
                                                background: "var(--border-subtle)", overflow: "hidden", position: "relative",
                                              }}>
                                                <div style={{
                                                  height: "100%", borderRadius: 5,
                                                  width: `${tlProgress}%`, background: tlBarColor,
                                                  transition: "width 300ms ease",
                                                }} />
                                              </div>
                                              <div style={{
                                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                                fontSize: 11, color: "var(--text-tertiary)", marginTop: 6,
                                              }}>
                                                <span><b style={{ fontWeight: 600, color: "var(--text-secondary)" }}>Start:</b> {fmtDate(startDate)}</span>
                                                <span style={{ fontWeight: 600, color: tlBarColor }}>{tlProgress}%</span>
                                                <span><b style={{ fontWeight: 600, color: "var(--text-secondary)" }}>Finish:</b> {fmtDate(finishDate)}</span>
                                              </div>
                                            </div>
                                          );
                                        })()}

                                        {/* ── 2. Phase Summary Timeline (E5 ribbon) ── */}
                                        {(() => {
                                          const pStart = String(
                                            (selectedItem as any)?.project?.start_date ||
                                            (selectedItem as any)?.start_date ||
                                            (selectedItem as any)?.project?.project_start_date ||
                                            (selectedItem as any)?.project_start_date || ""
                                          ).trim();
                                          const pEnd = String(
                                            (selectedItem as any)?.project?.finish_date ||
                                            (selectedItem as any)?.finish_date ||
                                            (selectedItem as any)?.project?.project_end_date ||
                                            (selectedItem as any)?.project_end_date || ""
                                          ).trim();
                                          return (
                                            <PhaseSummaryTimeline
                                              tasks={scheduleTasks}
                                              projectStart={pStart || null}
                                              projectEnd={pEnd || null}
                                              projectGuid={String(
                                                (selectedItem as any)?.project_guid ||
                                                (selectedItem as any)?.project_GUID ||
                                                (selectedItem as any)?.projectGuid || ''
                                              ).trim()}
                                              canEdit={canEdit}
                                            />
                                          );
                                        })()}

                                        {/* ── 3. Status reports (DEV-1296 Slice C) — dated narrative
                                              + photos, replaces the placeholder narrative. ── */}
                                        <ProjectStatusReportCard
                                          projectGuid={String(
                                            selectedItem?.project_guid ||
                                            selectedItem?.project?.project_guid ||
                                            selectedItem?.project_GUID ||
                                            selectedItem?.projectGuid || ''
                                          ).trim()}
                                          canEdit={!isGroup && canEditProjectContent(
                                            String(currentUserGuid ?? ''),
                                            String(selectedItem?.project_guid ?? ''),
                                          )}
                                        />

                                        {/* ── 4. Milestones + Mission Status (2-up row) ── */}
                                        <div className={styles.summaryTwoUp}>
                                          <MilestoneList
                                            projectGuid={String(
                                              selectedItem?.project_guid ||
                                              selectedItem?.project?.project_guid ||
                                              selectedItem?.project_GUID ||
                                              selectedItem?.projectGuid || ''
                                            ).trim()}
                                            tasks={scheduleTasks}
                                            canEdit={!isGroup && canEditProjectContent(
                                              String(currentUserGuid ?? ''),
                                              String(selectedItem?.project_guid ?? ''),
                                            )}
                                          />
                                          <MissionStatusCard selectedItem={selectedItem} />
                                        </div>

                                        {/* ── 5. Key Risks & Issues ── */}
                                        <RisksIssuesPlaceholder
                                          projectGuid={String(selectedItem?.project_guid ?? '')}
                                          canEdit={!isGroup && canEditProjectContent(
                                            String(currentUserGuid ?? ''),
                                            String(selectedItem?.project_guid ?? ''),
                                          )}
                                        />

                                        {/* Debug-mode tip removed — debug affordance now lives in the gear menu. */}

                                        {debugMode && (
                                          <DebugTextarea
                                            title="Summary debug (metadata + KPI definitions/values)"
                                            data={{
                                              project_guid: selectedItem?.project_guid,
                                              reporting_date: (selectedItem as any)?.reporting_date,
                                              kpi_mode: (selectedItem as any)?.kpi_mode,
                                              kpi_as_of_date: (selectedItem as any)?.kpi_as_of_date,

                                              // Standard + custom metadata
                                              project: selectedItem?.project ?? null,
                                              custom: (selectedItem as any)?.custom ?? null,

                                              // KPI outputs
                                              summary: {
                                                indicator_color: (selectedItem as any)?.summary_indicator_color,
                                                indicator_trend: (selectedItem as any)?.summary_indicator_trend,
                                                score_0_100: (selectedItem as any)?.summary_score_0_100,
                                                as_of_date: (selectedItem as any)?.summary_as_of_date,
                                                explain: (selectedItem as any)?.summary_explain,
                                              },
                                              schedule: {
                                                indicator_color: (selectedItem as any)?.schedule_indicator_color,
                                                indicator_trend: (selectedItem as any)?.schedule_indicator_trend,
                                                score_0_100: (selectedItem as any)?.schedule_score_0_100,
                                                as_of_date: (selectedItem as any)?.schedule_as_of_date,
                                                explain: (selectedItem as any)?.schedule_explain,
                                              },
                                              finance: {
                                                indicator_color: (selectedItem as any)?.finance_indicator_color,
                                                indicator_trend: (selectedItem as any)?.finance_indicator_trend,
                                                score_0_100: (selectedItem as any)?.finance_score_0_100,
                                                as_of_date: (selectedItem as any)?.finance_as_of_date,
                                                explain: (selectedItem as any)?.finance_explain,
                                              },
                                              mission: {
                                                indicator_color: (selectedItem as any)?.mission_indicator_color,
                                                indicator_trend: (selectedItem as any)?.mission_indicator_trend,
                                                score_0_100: (selectedItem as any)?.mission_score_0_100,
                                                as_of_date: (selectedItem as any)?.mission_as_of_date,
                                                subkpis: (selectedItem as any)?.mission_subkpis,
                                                explain: (selectedItem as any)?.mission_explain,
                                                targets: (selectedItem as any)?.mission_targets,
                                              },
                                              data_reliability: {
                                                color: (selectedItem as any)?.data_reliability_color,
                                                trend: (selectedItem as any)?.data_reliability_trend,
                                                score_0_100: (selectedItem as any)?.data_reliability_score_0_100,
                                                as_of_date: (selectedItem as any)?.data_reliability_as_of_date,
                                                // DEV-851 (PR2): engine v2 composites for diagnostic depth.
                                                now: (selectedItem as any)?.data_reliability_now,
                                                outlook: (selectedItem as any)?.data_reliability_outlook,
                                                is_completed_project: (selectedItem as any)?._isCompletedProject,
                                              },

                                              // KPI definitions (authoritative config)
                                              kpi_definitions: {
                                                summary_weights: (kpiConfig as any)?.summary?.weights ?? null,
                                                schedule: (kpiConfig as any)?.schedule ?? null,
                                                mission_subkpis: (kpiConfig as any)?.mission?.subkpis ?? null,
                                                data_reliability: (kpiConfig as any)?.data_reliability ?? null,
                                              },
                                            }}
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </TabPanel>
                    ))
                  }
                </div>
              </Tabs>
            </main>
          </div>
        ) : (
          <div className={styles.noData}>NO DATA...</div>
        )
      }

      {editProject ? (
        <ProjectConfigModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          project={editProject}
          onSaved={onSaved}
        />
      ) : null}

      {/* DEV-822 / DEV-831: HSE Report Incident modal. Opens from any
          HSE card's "+ Report" button, pre-filled with that card's
          category. Saves a ProjectIssue with subtype='hse_incident' and
          invalidates both the HSE incidents and standard issues caches
          on success so the card and the standard issues list both refresh.
          projectName breadcrumb (DEV-822) is stress-context confirmation
          that the incident is being filed against the right project — for
          users with multiple tabs open during a crisis. */}
      <ReportIncidentModal
        open={hseReportOpen}
        projectGuid={projectGuidStr}
        projectName={String(
          (selectedItem as any)?.project?.project_name ||
          (selectedItem as any)?.project_name ||
          ''
        ).trim() || undefined}
        initialCategory={hseReportCategory}
        onClose={() => setHseReportOpen(false)}
      />

      {/* DEV-822 / #128: Edit-incident modal. Opens when the user clicks
          an incident row in the IncidentDrillDown panel. PATCH is
          permission-gated server-side (Lead/Admin/Officer/Creator/Owner). */}
      <EditIncidentModal
        issueId={editingIncidentId}
        projectGuid={projectGuidStr}
        projectName={String(
          (selectedItem as any)?.project?.project_name ||
          (selectedItem as any)?.project_name ||
          ''
        ).trim() || undefined}
        onClose={() => setEditingIncidentId(null)}
        // DEV-847: pass current user so the modal can decide whether to show
        // the system-admin-only Delete button. onDeleted fires after a
        // successful delete so we close the modal cleanly.
        currentUserGuid={currentUserGuid}
        onDeleted={() => setEditingIncidentId(null)}
      />

      {/* DEV-822: HSE configuration tray. Opens from the pencil button on
          each HSE card. Reuses MissionKpiWizard which is already trimmed
          for incident_count (Step 2 only renders the relevant fields,
          Step 3 hides cadence/permissible-days, hse_category locked). */}
      {hseConfigCard && (() => {
        const ov = getProjectKpiConfigOverride(projectGuidStr as any);
        const list = (ov?.category_instances?.mission ?? []) as SubKpiInstance[];
        const editingInstance = list.find((i) => {
          const k = i.outcome_id || i.instance_guid || i.display_name || '';
          return String(k).toUpperCase() === String(hseConfigCard.sub_kpi_guid).toUpperCase();
        });
        if (!editingInstance) {
          // Defensive: card lookup miss. Close tray rather than crash.
          // Could happen mid-rollout if backfill is incomplete.
          console.warn('[hse] config tray: no SubKpiInstance found for', hseConfigCard.sub_kpi_guid);
          setHseConfigCard(null);
          return null;
        }
        return (
          <Tray open={true} onClose={() => setHseConfigCard(null)} width={760}>
            <MissionKpiWizard
              mode="edit"
              existingInstance={editingInstance}
              projectGuid={projectGuidStr}
              onSave={(updated: SubKpiInstance) => {
                // Mirror MissionSubKpiCard.handleConfigSave: update the
                // local override store, preserve weight + is_mandatory,
                // dispatch cache invalidation so engine recomputes.
                const currentOv = getProjectKpiConfigOverride(projectGuidStr as any);
                const existing = (currentOv?.category_instances?.mission ?? []) as SubKpiInstance[];
                const idx = existing.findIndex((i) => {
                  const oldGuid = String(i.instance_guid || '').toUpperCase();
                  const newGuid = String(updated.instance_guid || '').toUpperCase();
                  return oldGuid && oldGuid === newGuid;
                });
                let nextMission = [...existing];
                if (idx >= 0) {
                  const prior = existing[idx];
                  nextMission[idx] = {
                    ...updated,
                    weight: updated.weight ?? prior.weight,
                    is_mandatory: prior.is_mandatory,
                  } as SubKpiInstance;
                } else {
                  nextMission.push(updated);
                }
                upsertProjectKpiConfigOverride({
                  project_guid: projectGuidStr as any,
                  category_instances: {
                    ...(currentOv?.category_instances ?? {}),
                    mission: nextMission,
                  } as any,
                } as any);
                // invalidateAndRefetch (not raw dispatchEvent) — see
                // queryCache.ts: subscribers only refetch when the cache
                // entry is also marked stale.
                invalidateAndRefetch(QUERY_KEYS.mission);
                setHseConfigCard(null);
              }}
              onCancel={() => setHseConfigCard(null)}
            />
          </Tray>
        );
      })()}
    </>
  );
}
export default KpiNavigation;
